import React, { useState, useRef, Component } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function ConsularBanner() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const {
    t,
    language
  } = useLanguage();
  const [modalOpen, setModalOpen] = useState(false);
  const [consularModule, setConsularModule] = useState<{
    tips: any[];
    TipCard: ComponentType<any>;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const isEs = language === 'ES';
  const handleOpenModal = async () => {
    setModalOpen(true);
    if (!consularModule) {
      setLoading(true);
      try {
        const mod = await import('./ConsularAffairs');
        setConsularModule({
          tips: mod.tips,
          TipCard: mod.TipCard
        });
      } catch (err) {
        console.error('Failed to load consular module:', err);
      } finally {
        setLoading(false);
      }
    }
  };
  return <section ref={ref} className="bg-haa-blue py-10 px-4" aria-label="Consular Affairs quick link" data-id="element-2185" data-mp="dyki29w0bisk">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={inView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1]
    }} className="flex flex-col items-center gap-4 text-center" data-id="element-2186" data-mp="8yzdnjy2ii8g">
        <p className="font-nunito text-white/80 text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-2187" data-mp="3mfyp839j7t27">
          {t('consularBanner.label')}
        </p>
        <h2 className="font-bebas text-4xl sm:text-5xl text-white tracking-wider leading-none" data-id="element-2188" data-mp="3hdzqj6oqlkbl">
          {t('consularBanner.heading')}
        </h2>
        <motion.button onClick={handleOpenModal} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.97
      }} className="inline-flex items-center gap-2 bg-white text-haa-blue font-nunito font-bold text-sm px-8 py-3 rounded-full shadow-lg hover:bg-haa-skyblue hover:text-white transition-all duration-300 mt-2" data-id="element-2189" data-mp="38g7a1z7a46mm">
          {t('consularBanner.button')}
          <ArrowRightIcon className="w-4 h-4" data-id="element-2190" data-mp="2tua6586mjr6i" />
        </motion.button>
      </motion.div>

      <AnimatePresence data-id="element-2191" data-mp="3hk4i7nj4dbpp">
        {modalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm" onClick={() => setModalOpen(false)} data-id="element-2192" data-mp="3ljv35alysxmb">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="bg-gray-50 rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-2193" data-mp="941bbjztzr2f">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white" data-id="element-2194" data-mp="4hii718kchm4">
                <div data-id="element-2195" data-mp="1kuz7zho02n5w">
                  <h2 className="font-bebas text-2xl sm:text-3xl text-haa-navy tracking-wider" data-id="element-2196" data-mp="1kufs06hwu8ui">
                    {t('consular.heading')}
                  </h2>
                  <p className="font-nunito text-gray-500 text-sm mt-1" data-id="element-2197" data-mp="3lky2jeu2hok6">
                    {isEs ? 'Información y Servicios Consulares' : 'Consular Information & Services'}
                  </p>
                </div>
                <button onClick={() => setModalOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-2198" data-mp="3vhko65u7wjn9">
                  <XIcon className="w-6 h-6" data-id="element-2199" data-mp="dcpppv9s6gpl" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6" data-id="element-2200" data-mp="i50rl3o30cks">
                <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-id="element-2201" data-mp="n32iubvnvi7c">
                  <p className="font-nunito text-amber-800 text-xs sm:text-sm leading-relaxed" data-id="element-2202" data-mp="r4nvpx873whj">
                    <span className="font-bold" data-id="element-2203" data-mp="w3qoai6zoa8m">
                      {isEs ? 'Aviso: ' : 'Disclaimer: '}
                    </span>
                    {isEs ? 'La Asociación Hondureño-Americana no está afiliada ni conectada de ninguna manera con la Embajada de Honduras, los Consulados de Honduras ni la Cancillería (Secretaría de Relaciones Exteriores). Esta información se proporciona únicamente con fines informativos. Para trámites oficiales, comuníquese directamente con su consulado más cercano.' : 'The Honduran-American Association is not affiliated with or connected in any way to the Honduran Embassy, the Honduran Consulates, or the Foreign Ministry. This information is provided for informational purposes only. For official procedures, please contact your nearest consulate directly.'}
                  </p>
                </div>
                {loading ? <div className="flex items-center justify-center py-16" data-id="element-2204" data-mp="3j0s05bl3q65q">
                    <div className="w-8 h-8 border-3 border-haa-blue/20 border-t-haa-blue rounded-full animate-spin" data-id="element-2205" data-mp="3e9jpydby6gri" />
                  </div> : consularModule ? <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6" data-id="element-2206" data-mp="12h95acld3hg">
                    {consularModule.tips.map((tip, i) => <consularModule.TipCard key={tip.titleKey + i} tip={tip} index={i} data-id="element-2207" data-mp="3oe9umqyqjyzf" data-mpi={i} />)}
                  </div> : null}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-white flex items-center justify-between" data-id="element-2208" data-mp="3jenfud39eaoi">
                <a href="#consular-affairs" onClick={e => {
              e.preventDefault();
              setModalOpen(false);
              setTimeout(() => {
                const el = document.getElementById('consular-affairs');
                if (el) {
                  const navbarHeight = 88;
                  const pos = el.getBoundingClientRect().top + window.scrollY;
                  window.scrollTo({
                    top: pos - navbarHeight,
                    behavior: 'smooth'
                  });
                }
              }, 350);
            }} className="font-nunito text-haa-blue hover:text-haa-navy text-sm font-semibold hover:underline transition-colors inline-flex items-center gap-1" data-id="element-2209" data-mp="35fxlv6bqkr4d">
                  {isEs ? 'Ver Sección Completa ↓' : 'View Full Section ↓'}
                </a>
                <button onClick={() => setModalOpen(false)} className="px-6 py-2.5 bg-haa-navy hover:bg-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-colors" data-id="element-2210" data-mp="3ku9fe3p6m06g">
                  {isEs ? 'Cerrar' : 'Close'}
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}